import {
  IAbstractScoreboard,
  IEvent,
  IGaelicScoreboard,
  IGame,
  ILocalRef,
  IScoreboardScore,
  IUpdateSetBasedScoreboardScoresModification,
  IUpdateTeamGameScoreboardScoreModification,
  IUpsertScoreboardModification,
  IUserModification
} from "@webng-types/write-model";

export function isHomeTeam(game: IGame, event: IEvent): boolean {
  if (game.scoreboard && "home_team" in game.scoreboard && game.scoreboard.home_team) {
    if (event.sport_event?.team) {
      return game.scoreboard.home_team.local_id === event.sport_event.team.local_id;
    }
  }
  return false;
}

export function isAwayTeam(game: IGame, event: IEvent): boolean {
  if (game.scoreboard && "away_team" in game.scoreboard && game.scoreboard.away_team) {
    if (event.sport_event?.team) {
      return game.scoreboard.away_team.local_id === event.sport_event.team.local_id;
    }
  }
  return false;
}

export function addCardToScoreboard(game: IGame, event: IEvent, isNewEvent: boolean, cardType: "red" | "black"): IUserModification[] {
  if (!isNewEvent) return [];

  let key: "home_red_cards" | "away_red_cards" | "home_black_cards" | "away_black_cards";
  if (isHomeTeam(game, event) && cardType === "red") key = "home_red_cards";
  else if (isHomeTeam(game, event) && cardType === "black") key = "home_black_cards";
  else if (isAwayTeam(game, event) && cardType === "red") key = "away_red_cards";
  else if (isAwayTeam(game, event) && cardType === "black") key = "away_black_cards";
  else return [];

  console.log(`found key ${key}`)

  // only accept when scoreboard accepts key
  if (!game.scoreboard || !(key in game.scoreboard)) return [];

  let cards: ILocalRef[] = []
  if (game.scoreboard && key in game.scoreboard && (game.scoreboard as IGaelicScoreboard)[key]) {
    cards = (game.scoreboard as IGaelicScoreboard)[key]!;
  }

  cards.push(createCard(event));

  const scoreboard = {
    ...game.scoreboard,
    [key]: cards,
  }

  return [createUpsertScoreboardModification(scoreboard, event)];
}

export function addGaelicScoreToScoreboard(game: IGame, event: IEvent, isNewEvent: boolean, type: "goal" | "point" | "two-point"): IUserModification[] {
  if (isNewEvent && game.scoreboard && game.scoreboard._type === "Tik::Model::Content::Scoreboard::GaelicScoreboard" && event && event.sport_event) {

    let homePoints = game.scoreboard.home_points ?? 0;
    let homeGoals = game.scoreboard.home_goals ?? 0;
    let awayPoints = game.scoreboard.away_points ?? 0;
    let awayGoals = game.scoreboard.away_goals ?? 0;

    if (isHomeTeam(game, event) && type === "point") homePoints++;
    else if (isHomeTeam(game, event) && type === "two-point") homePoints += 2;
    else if (isHomeTeam(game, event) && type === "goal") homeGoals++;
    else if (isAwayTeam(game, event) && type === "point") awayPoints++;
    else if (isAwayTeam(game, event) && type === "two-point") awayPoints += 2;
    else if (isAwayTeam(game, event) && type === "goal") awayGoals++;
    else return [];

    const scoreboard = {
      ...game.scoreboard,
      home_points: homePoints,
      home_goals: homeGoals,
      away_points: awayPoints,
      away_goals: awayGoals
    }

    return [createUpsertScoreboardModification(scoreboard, event)];
  } else return [];
}

export function scoreChange(scoreChange: number): (game: IGame, event: IEvent, isNewEvent: boolean) => IUserModification[] {
  return (game: IGame, event: IEvent, isNewEvent: boolean): IUserModification[] => {
    if(isNewEvent && game.scoreboard && game.scoreboard && game.scoreboard._type === "Tik::Model::Content::Scoreboard::TeamGameScoreboard" && game.scoreboard.score && event && event.sport_event && event.sport_event.team && !event.sport_event.is_cancelled) {
      const isHome = isHomeTeam(game, event)

      const newScore = addToScore(game.scoreboard.score, scoreChange, isHome, 0)
      return [makeUpdateTeamGameScoreboardScoreModification(newScore)];
    } else return [];
  }
}

export function scoreChangeOpponent(scoreChange: number): (game: IGame, event: IEvent, isNewEvent: boolean) => IUserModification[] {
  return (game: IGame, event: IEvent, isNewEvent: boolean): IUserModification[] => {
    if(isNewEvent && game.scoreboard && game.scoreboard && game.scoreboard._type === "Tik::Model::Content::Scoreboard::TeamGameScoreboard" && game.scoreboard.score && event && event.sport_event && event.sport_event.team && !event.sport_event.is_cancelled) {
      const isAway = isAwayTeam(game, event)

      const newScore = addToScore(game.scoreboard.score, scoreChange, isAway, 0)
      return [makeUpdateTeamGameScoreboardScoreModification(newScore)];
    } else return [];
  }
}

export function initializeTeamGameScoreboard(game: IGame, event: IEvent, isNewEvent: boolean): IUserModification[] {
  if(isNewEvent && game.scoreboard && game.scoreboard && game.scoreboard._type === "Tik::Model::Content::Scoreboard::TeamGameScoreboard" && game.scoreboard.score && event && event.sport_event) {
    return [
      makeUpdateTeamGameScoreboardScoreModification({
        ...game.scoreboard.score,
        home_score: ensureScoreValue(game.scoreboard.score.home_score),
        away_score: ensureScoreValue(game.scoreboard.score.away_score)
      })
    ]
  }
  return [];
}

export function initializeGaelicScoreboard(game: IGame, event: IEvent, isNewEvent: boolean): IUserModification[] {
  if (isNewEvent) {
    const scoreboard = {
      _type: "Tik::Model::Content::Scoreboard::GaelicScoreboard",
      home_goals: 0,
      home_points: 0,
      away_goals: 0,
      away_points: 0,
      home_red_cards: [],
      home_black_cards: [],
      away_red_cards: [],
      away_black_cards: [],
      ...game.scoreboard,
    } as IGaelicScoreboard
    return [createUpsertScoreboardModification(scoreboard, event)];
  } else return [];
}

export function createCard(event: IEvent): ILocalRef {
  return {
    _type: "Tik::Model::LocalRef",
    created_at: event.created_at,
    local_id: event.local_id,
    local_status: event.local_status,
    version: event.version,
    sort: event.sort
  }
}

export function createUpsertScoreboardModification(scoreboard: IAbstractScoreboard, event: IEvent): IUpsertScoreboardModification {
  return {
    _type: "Tik::Model::Modification::UpsertScoreboardModification",
    scoreboard: scoreboard,
  }
}



export function addToScore(score: IScoreboardScore, addValue: number, isHome: boolean, initialValue: number = 0){
  const homeScore = isHome ? addToScoreValue(score.home_score, addValue, addValue) : ensureScoreValue(score.home_score, initialValue.toString());
  const awayScore = isHome ? ensureScoreValue(score.away_score, initialValue.toString()) : addToScoreValue(score.away_score, addValue, addValue);
  return {...score, home_score: homeScore, away_score: awayScore};
}

export function addToScoreValue(scoreValue: string | undefined, addValue: number, initialValue: number = 0) {
  let newScoreValue;

  if (scoreValue === undefined || scoreValue === "" || scoreValue === "-") {
    newScoreValue = initialValue;
  } else {
    const currentScore = Number.parseInt(scoreValue, 10);
    if(!Number.isNaN(currentScore)){
      newScoreValue = (currentScore + addValue);
    } else {
      newScoreValue = initialValue;
    }
  }

  return newScoreValue !== undefined ? newScoreValue.toString() : scoreValue;
}

export function ensureScoreValue(score: string | undefined, initialValue: string = "0"){
  if (score === undefined || score === "" || score === "-") {
    return initialValue;
  } else {
    return score;
  }
}

export function makeUpdateTeamGameScoreboardScoreModification(score: IScoreboardScore): IUpdateTeamGameScoreboardScoreModification{
  return {
    _type: "Tik::Model::Modification::UpdateTeamGameScoreboardScoreModification",
    score: score
  }
}

export function makeUpdateSetBasedScoreboardScoreModification(scores: IScoreboardScore[]): IUpdateSetBasedScoreboardScoresModification{
  return {
    _type: "Tik::Model::Modification::UpdateSetBasedScoreboardScoresModification",
    scores: scores
  }
}
